import { Config } from '@src/core/Config';
import type { GameLoop } from '@src/core/GameLoop';
import type { GameSystem } from '@src/ecs/System';
import type { GameContext } from '@src/ecs/world';
import type { ArenaCamera } from '@src/graphics/Camera';

/**
 * Consumes ctx.fx once per frame: hit-stop freezes the sim, directional shake
 * offsets the camera, rumble drives every connected gamepad.
 */
export class ScreenFx implements GameSystem {
  private ctx!: GameContext;
  private shakeX = 0;
  private shakeY = 0;
  private velX = 0;
  private velY = 0;
  private rumbleCooldown = 0;

  constructor(
    private readonly loop: GameLoop,
    private readonly camera: ArenaCamera,
  ) {}

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {
    this.camera.setShake(0, 0);
  }

  update(frameDt: number): void {
    const fx = this.ctx.fx;

    if (fx.hitStopMs > 0) {
      this.loop.freeze(fx.hitStopMs);
      fx.hitStopMs = 0;
    }

    // Impulses kick the shake velocity in the hit direction; a damped spring brings it home.
    this.velX += fx.shakeX * 22;
    this.velY += fx.shakeY * 22;
    fx.shakeX = 0;
    fx.shakeY = 0;
    const k = 140;
    const damp = Config.fx.shakeDecay * 2.2;
    this.velX += (-this.shakeX * k - this.velX * damp) * frameDt;
    this.velY += (-this.shakeY * k - this.velY * damp) * frameDt;
    this.shakeX += this.velX * frameDt;
    this.shakeY += this.velY * frameDt;
    const cap = 0.9;
    if (this.shakeX > cap) this.shakeX = cap;
    if (this.shakeX < -cap) this.shakeX = -cap;
    if (this.shakeY > cap) this.shakeY = cap;
    if (this.shakeY < -cap) this.shakeY = -cap;
    this.camera.setShake(this.shakeX, this.shakeY);

    this.rumbleCooldown -= frameDt;
    if (fx.rumble > 0) {
      if (this.rumbleCooldown <= 0) {
        this.rumble(fx.rumble);
        this.rumbleCooldown = Config.fx.rumbleMs / 1000;
      }
      fx.rumble = 0;
    }
  }

  private rumble(strength: number): void {
    if (typeof navigator.getGamepads !== 'function') return;
    const pads = navigator.getGamepads();
    for (let i = 0; i < pads.length; i++) {
      const actuator = pads[i]?.vibrationActuator;
      if (!actuator || typeof actuator.playEffect !== 'function') continue;
      void actuator
        .playEffect('dual-rumble', {
          duration: Config.fx.rumbleMs,
          strongMagnitude: Math.min(1, strength),
          weakMagnitude: Math.min(1, strength * 0.6),
        })
        .catch(() => undefined);
    }
  }
}
